/**
 * Fishing Manager
 * 
 * Handles fishing spot interactions, minigame UI management,
 * and fishing-related state.
 */

import Phaser from 'phaser';
import { Socket } from 'socket.io-client';
import { TILE_SIZE } from '../types';
import { FishingSpotInfo } from '../systems/FishingSpotRenderer';
import { FishingMinigameUI } from '../systems/FishingMinigameUI';

export interface FishingManagerCallbacks {
  getLocalShip: () => Phaser.GameObjects.Container | null;
  getSocket: () => Socket | null;
  moveToPosition: (worldX: number, worldY: number) => void;
  disengageTarget: () => void;
}

export class FishingManager {
  private scene: Phaser.Scene;
  private callbacks: FishingManagerCallbacks;
  private fishingMinigameUI: FishingMinigameUI;
  
  // Fishing state
  private isFishing: boolean = false;
  private fishingMinigameActive: boolean = false;
  private currentFishingSpotType: string = 'shallow_waters';
  private currentFishingSpotId: string = '';
  private pendingFishingSpot: FishingSpotInfo | null = null;
  private fishingStatusUI: Phaser.GameObjects.Container | null = null;

  constructor(scene: Phaser.Scene, callbacks: FishingManagerCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
    
    this.fishingMinigameUI = new FishingMinigameUI(scene, {
      onCatchComplete: (quality: number) => {
        console.log(`🎣 Fishing minigame complete! Quality: ${quality}%`);
        const socket = this.callbacks.getSocket();
        socket?.emit('fishing:minigameComplete', { 
          spotId: this.currentFishingSpotId,
          quality 
        });
        this.fishingMinigameActive = false;
        // Ensure UI closes after a successful catch attempt
        this.fishingMinigameUI.deactivate();
      },
      onCatchFailed: () => {
        console.log(`🎣 Fishing minigame failed - no bonus`);
        this.fishingMinigameActive = false;
        this.fishingMinigameUI.deactivate();
      },
      onDeactivate: () => {
        this.fishingMinigameActive = false;
      },
    });
  }

  // ============ GETTERS ============

  getIsFishing(): boolean {
    return this.isFishing;
  }

  setIsFishing(isFishing: boolean): void {
    this.isFishing = isFishing;
  }

  getFishingMinigameUI(): FishingMinigameUI {
    return this.fishingMinigameUI;
  }

  isFishingMinigameActive(): boolean {
    return this.fishingMinigameUI.isActive();
  }

  getCurrentFishingSpotType(): string {
    return this.currentFishingSpotType;
  }

  getCurrentFishingSpotId(): string {
    return this.currentFishingSpotId;
  }

  getPendingFishingSpot(): FishingSpotInfo | null {
    return this.pendingFishingSpot;
  }

  clearPendingFishingSpot(): void {
    this.pendingFishingSpot = null;
  }

  // ============ FISHING INTERACTIONS ============

  /**
   * Handle Fishing Spot click - move to adjacent tile and start fishing
   */
  handleFishingSpotClick(spot: FishingSpotInfo): void {
    const localShip = this.callbacks.getLocalShip();
    if (!localShip) return;
    
    const playerTileX = Math.floor(localShip.x / TILE_SIZE);
    const playerTileY = Math.floor(localShip.y / TILE_SIZE);
    
    const distX = Math.abs(playerTileX - spot.tileX);
    const distY = Math.abs(playerTileY - spot.tileY);
    const isAdjacent = distX <= 1 && distY <= 1 && !(distX === 0 && distY === 0);
    
    if (isAdjacent) {
      this.startFishing(spot);
    } else {
      const adjacentTiles = [
        { x: spot.tileX - 1, y: spot.tileY },
        { x: spot.tileX + 1, y: spot.tileY },
        { x: spot.tileX, y: spot.tileY - 1 },
        { x: spot.tileX, y: spot.tileY + 1 },
      ];
      
      let closestTile = adjacentTiles[0];
      let closestDist = Infinity;
      
      for (const tile of adjacentTiles) {
        const dist = Math.abs(tile.x - playerTileX) + Math.abs(tile.y - playerTileY);
        if (dist < closestDist) {
          closestDist = dist;
          closestTile = tile;
        }
      }
      
      const targetX = closestTile.x * TILE_SIZE + TILE_SIZE / 2;
      const targetY = closestTile.y * TILE_SIZE + TILE_SIZE / 2;
      
      this.callbacks.disengageTarget();
      this.callbacks.moveToPosition(targetX, targetY);
      
      this.pendingFishingSpot = spot;
    }
  }

  /**
   * Handle fishing spot right-click - emit event to show research panel
   */
  handleFishingSpotRightClick(spot: FishingSpotInfo, screenX: number, screenY: number): void {
    console.log(`🔍 Fishing spot right-clicked: ${spot.type} at (${spot.tileX}, ${spot.tileY})`);
    this.scene.events.emit('fishingspot:research', {
      spotId: spot.id,
      spotType: spot.type,
      spotPosition: { x: spot.worldX, y: spot.worldY },
      tilePosition: { x: spot.tileX, y: spot.tileY },
      screenPosition: { x: screenX, y: screenY },
    });
  }

  /**
   * Start fishing at a spot
   */
  startFishing(spot: FishingSpotInfo): void {
    console.log(`🎣 Starting to fish at ${spot.type} spot (${spot.tileX}, ${spot.tileY}) [id: ${spot.id}]`);
    
    this.showFishingCastingIndicator(spot);
    
    const socket = this.callbacks.getSocket();
    socket?.emit('fishing:start', { spotId: spot.id });
  }

  /**
   * Toggle fishing minigame - only works when fishing
   */
  toggleFishingMinigame(): void {
    if (!this.isFishing) {
      console.log('🎣 Cannot activate fishing minigame - not fishing');
      return;
    }
    
    if (this.fishingMinigameUI.isActive()) {
      this.fishingMinigameUI.deactivate();
    } else {
      this.fishingMinigameUI.activate(this.currentFishingSpotType, this.currentFishingSpotId);
      this.fishingMinigameActive = true;
      console.log('🎣 Fishing minigame activated - press SPACE to keep bobber in zone, ESC to close');
    }
  }

  /**
   * Activate fishing minigame (called after a successful catch)
   */
  activateFishingMinigame(): void {
    if (!this.isFishing) {
      return;
    }
    
    if (!this.fishingMinigameUI.isActive()) {
      this.fishingMinigameUI.activate(this.currentFishingSpotType, this.currentFishingSpotId);
      this.fishingMinigameActive = true;
      console.log('🎣 Fishing minigame activated after catch - press SPACE to keep bobber in zone, ESC to close');
    }
  }

  /**
   * Update fishing minigame (called from scene update)
   */
  update(delta: number): void {
    this.fishingMinigameUI.update(delta);
  }

  // ============ FISHING UI ============

  /**
   * Show casting indicator when clicking a fishing spot
   */
  private showFishingCastingIndicator(spot: FishingSpotInfo): void {
    const indicator = this.scene.add.container(spot.worldX, spot.worldY);
    indicator.setDepth(150);
    
    const ripple = this.scene.add.graphics();
    ripple.lineStyle(2, 0x5dade2, 1);
    ripple.strokeCircle(0, 0, 8);
    indicator.add(ripple);
    
    (this.scene as any).tweens.add({
      targets: ripple,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 600,
      ease: 'Cubic.easeOut',
      onComplete: () => indicator.destroy(),
    });
    
    const localShip = this.callbacks.getLocalShip();
    if (localShip) {
      const castText = this.scene.add.text(localShip.x, localShip.y - 30, 'Casting...', {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '10px',
        color: '#5dade2',
        backgroundColor: '#00000088',
        padding: { x: 4, y: 2 },
      });
      castText.setOrigin(0.5);
      castText.setDepth(150);
      
      (this.scene as any).tweens.add({
        targets: castText,
        alpha: 0,
        y: localShip.y - 50,
        duration: 1000,
        delay: 500,
        onComplete: () => castText.destroy(),
      });
    }
  }

  /**
   * Show fishing status UI
   */
  showFishingStatus(message: string, isActive: boolean): void {
    this.hideFishingStatus();
    
    if (!isActive) return;
    
    const { width, height } = this.scene.cameras.main;
    
    this.fishingStatusUI = this.scene.add.container(width / 2, height - 100);
    this.fishingStatusUI.setScrollFactor(0);
    this.fishingStatusUI.setDepth(200);
    
    const bg = this.scene.add.rectangle(0, 0, 200, 40, 0x000000, 0.7);
    bg.setStrokeStyle(2, 0x5dade2);
    this.fishingStatusUI.add(bg);
    
    const text = this.scene.add.text(0, 0, message, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '12px',
      color: '#5dade2',
    });
    text.setOrigin(0.5);
    this.fishingStatusUI.add(text);
  }

  /**
   * Hide fishing status UI
   */
  hideFishingStatus(): void {
    if (this.fishingStatusUI) {
      this.fishingStatusUI.destroy();
      this.fishingStatusUI = null;
    }
  }

  /**
   * Show fishing message (catch, no catch, etc)
   */
  showFishingMessage(message: string, color: string): void {
    const localShip = this.callbacks.getLocalShip();
    if (!localShip) return;
    
    const text = this.scene.add.text(localShip.x, localShip.y - 40, message, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '12px',
      color: color,
      backgroundColor: '#00000088',
      padding: { x: 4, y: 2 },
    });
    text.setOrigin(0.5);
    text.setDepth(150);
    
    (this.scene as any).tweens.add({
      targets: text,
      alpha: 0,
      y: localShip.y - 70,
      duration: 1500,
      onComplete: () => text.destroy(),
    });
  }

  /**
   * Show fishing catch notification
   */
  showFishingCatch(fish: { id: string; name: string; category: string; value: number }, message: string, color: string): void {
    const localShip = this.callbacks.getLocalShip();
    if (!localShip) return;
    
    const container = this.scene.add.container(localShip.x, localShip.y - 50);
    container.setDepth(150);
    
    const bg = this.scene.add.rectangle(0, 0, 160, 50, 0x000000, 0.8);
    bg.setStrokeStyle(2, Phaser.Display.Color.HexStringToColor(color).color);
    container.add(bg);
    
    const nameText = this.scene.add.text(0, -12, fish.name, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '14px',
      color: color,
      fontStyle: 'bold',
    });
    nameText.setOrigin(0.5);
    container.add(nameText);
    
    const valueText = this.scene.add.text(0, 8, `${fish.value} berries`, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '10px',
      color: '#ffd700',
    });
    valueText.setOrigin(0.5);
    container.add(valueText);
    
    (this.scene as any).tweens.add({
      targets: container,
      alpha: 0,
      y: localShip.y - 100,
      duration: 2000,
      delay: 1000,
      onComplete: () => container.destroy(),
    });
  }

  /**
   * Show fishing error
   */
  showFishingError(message: string): void {
    this.showFishingMessage(message, '#ff6666');
    this.hideFishingStatus();
  }

  // ============ SOCKET HANDLERS ============

  /**
   * Handle fishing:started socket event
   */
  handleFishingStarted(data: { spotId: string; spotType: string; message: string }): void {
    console.log(`🎣 ${data.message}`);
    this.showFishingStatus(data.message, true);
    this.isFishing = true;
    this.currentFishingSpotType = data.spotType;
    this.currentFishingSpotId = data.spotId;
    this.fishingMinigameUI.setIsFishing(true, data.spotType, data.spotId);
  }

  /**
   * Handle fishing:stopped socket event
   */
  handleFishingStopped(data: { message: string }): void {
    console.log(`🎣 ${data.message}`);
    this.showFishingStatus(data.message, false);
    this.isFishing = false;
    // Always close the minigame UI when fishing ends to avoid stuck state
    this.fishingMinigameUI.deactivate();
    this.fishingMinigameUI.setIsFishing(false);
  }

  /**
   * Handle fishing:error socket event
   */
  handleFishingError(data: { message: string }): void {
    console.log(`❌ Fishing error: ${data.message}`);
    this.showFishingError(data.message);
    this.isFishing = false;
    this.fishingMinigameUI.deactivate();
    this.fishingMinigameUI.setIsFishing(false);
    const socket = this.callbacks.getSocket();
    socket?.emit('fishing:stop');
  }
}
